import { inject } from '@angular/core';
import { CanActivateFn, CanMatchFn, Router } from '@angular/router';
import { LocationService } from '@services/location.service';
import { environment } from 'src/environments/environment';
import { catchError, map, of } from 'rxjs';

const defaultLocationUuid = environment.defaultLocationUuid;

// Child paths of the 'location' route, used to detect short URLs without a UUID prefix
function getReservedPaths(router: Router): Set<string> {
  const children = router.config.find((r) => r.data?.['routeTag'] === 'location')?.children ?? [];
  return new Set(children.map((c) => c.path!));
}

// Looks up UUID from subdomain and redirects to /:uuid/<path>
function subdomainRedirect(router: Router, locationService: LocationService, pathSegments: string[]) {
  const queryParams = router.getCurrentNavigation()?.extractedUrl?.queryParams;
  const isRootPath = pathSegments.length === 0;
  const targetPath = isRootPath ? ['services'] : pathSegments;

  return locationService.fetchSubdomainLookup().pipe(
    map(({ uuid, isDebrandedDomain, subdomain }) => {
      if (uuid) {
        // debranded domain with location found via subdomain
        return router.createUrlTree([uuid, ...targetPath], { queryParams });
      }

      if (isDebrandedDomain) {
        // no location found
        return subdomain === 'www'
          ? router.createUrlTree([defaultLocationUuid, ...targetPath], { queryParams })
          : router.createUrlTree(['/page-not-found']);
      }

      if (isRootPath) {
        // in root, but not in debranded domain
        return router.createUrlTree([defaultLocationUuid, ...targetPath], { queryParams });
      }

      // everything else
      return router.createUrlTree(['/page-not-found']);
    }),
    catchError(() => of(router.createUrlTree(['/page-not-found']))),
  );
}

// Root path guard, redirects '/' to /:uuid/services
export const customSubdomainGuard: CanActivateFn = () => {
  return subdomainRedirect(inject(Router), inject(LocationService), []);
};

// Matches URLs starting with a reserved path, redirects to /:uuid/<path>
export const subdomainCatchAllGuard: CanMatchFn = (_route, segments) => {
  const router = inject(Router);

  if (!segments.length || !getReservedPaths(router).has(segments[0].path)) {
    return false;
  }

  return subdomainRedirect(
    router,
    inject(LocationService),
    segments.map((s) => s.path),
  );
};
